import React, { FC, useState, ChangeEvent } from 'react';
import { HiOutlinePlus, HiOutlineTrash } from 'react-icons/hi';
import { Button } from '../../../../../ui/Button';
import { Input } from '../../../../../commons';

interface AccommodationOption {
  id: string;
  name: string;
  price: number;
  salePrice?: number;
  isOnSale: boolean;
  spacesLeft?: number;
}

interface AccommodationManagerProps {
  accommodations: AccommodationOption[];
  onChange: (accommodations: AccommodationOption[]) => void;
}

const AccommodationManager: FC<AccommodationManagerProps> = ({ accommodations, onChange }) => {
  const [isAdding, setIsAdding] = useState(false);
  const [currentOption, setCurrentOption] = useState<Partial<AccommodationOption>>({
    name: '',
    price: 0,
    isOnSale: false,
    salePrice: 0,
    spacesLeft: 0,
  });

  const handleAdd = () => {
    if (!currentOption.name || !currentOption.price) return;
    
    const newOption = {
      ...currentOption,
    } as AccommodationOption;

    onChange([...accommodations, newOption]);
    setCurrentOption({ name: '', price: 0, isOnSale: false, salePrice: 0, spacesLeft: 0 });
    setIsAdding(false);
  };

  const handleRemove = (id: string) => {
    onChange(accommodations.filter((acc, index) => acc.id !== id && index.toString() !== id));
  };

  return (
    <div className='space-y-6'>
      <div className='flex justify-between items-center bg-gray-50/50 dark:bg-gray-800/30 p-3 rounded-xl border border-gray-100 dark:border-gray-700/50'>
        <div className='flex items-center gap-2'>
            <div className='w-2 h-2 rounded-full bg-primary-500' />
            <label className='text-sm font-bold text-gray-900 dark:text-gray-100 uppercase tracking-wider'>
              Accommodation Options
            </label>
        </div>
        {!isAdding && (
          <Button 
            variant='outline' 
            size='sm'
            onClick={() => setIsAdding(true)}
            leftIcon={<HiOutlinePlus className='w-4 h-4' />}
            className='!py-1.5 !px-3 font-bold text-xs'
          >
            Add Option
          </Button>
        )}
      </div>

      {/* List existing options */}
      <div className='grid grid-cols-1 sm:grid-cols-2 gap-4'>
        {accommodations.map((acc, index) => (
          <div key={acc.id || index} className='group relative p-4 bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 hover:border-primary-200 dark:hover:border-primary-500/30 hover:shadow-lg hover:shadow-primary-500/5 transition-all duration-300'>
            <div className='flex justify-between items-start mb-3'>
              <div>
                <p className='font-bold text-gray-900 dark:text-white'>{acc.name}</p>
              </div>
              <button
                type='button'
                onClick={() => handleRemove(acc.id || index.toString())}
                className='p-1.5 text-gray-400 hover:text-rose-500 hover:bg-rose-50 dark:hover:bg-rose-900/20 rounded-lg transition-all'
                title='Remove option'
              >
                <HiOutlineTrash className='w-4 h-4' />
              </button>
            </div>
            
            <div className='flex flex-wrap gap-2 items-center'>
              <div className='px-2.5 py-1 rounded-lg bg-gray-50 dark:bg-gray-700/50 text-xs font-bold text-gray-700 dark:text-gray-300'>
                ${acc.isOnSale ? acc.salePrice : acc.price}
                {acc.isOnSale && (
                  <span className='ml-1.5 line-through text-gray-400 font-normal'>${acc.price}</span>
                )}
              </div>
              {acc.isOnSale && (
                <span className='px-2 py-0.5 rounded-full bg-rose-100 text-rose-600 dark:bg-rose-900/30 dark:text-rose-400 text-[10px] font-bold uppercase'>Sale</span>
              )}
              {acc.spacesLeft !== undefined && acc.spacesLeft > 0 && (
                <span className='text-[10px] font-bold text-blue-500 uppercase tracking-tight'>
                   {acc.spacesLeft} Slots Left
                </span>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Add new form */}
      {isAdding && (
        <div className='p-6 bg-gray-50 dark:bg-gray-800/50 rounded-2xl border border-gray-200 dark:border-gray-700 space-y-5 animate-in fade-in slide-in-from-top-4 duration-300'>
            <div className='flex items-center gap-2 mb-2'>
                <h4 className='text-sm font-bold text-gray-900 dark:text-white'>Configure New Room Type</h4>
            </div>
            <div>
              <Input 
                label='Room Type Name' 
                name='acc_name' 
                value={currentOption.name}
                onChange={(e: any) => setCurrentOption({...currentOption, name: e.target.value})}
                placeholder='e.g. Twin Share, Deluxe Suite'
              />
            </div>
            <div className='grid grid-cols-2 gap-5'>
              <Input 
                label='Standard Price (USD)' 
                type='number'
                name='acc_price'
                value={currentOption.price}
                onChange={(e: any) => setCurrentOption({...currentOption, price: Number(e.target.value)})}
              />
               <Input 
                label='Sale Price (USD)' 
                type='number'
                name='acc_sale_price'
                value={currentOption.salePrice}
                onChange={(e: any) => setCurrentOption({...currentOption, salePrice: Number(e.target.value)})}
                note='Used only if Sale is enabled'
              />
            </div>
             <div className='flex flex-col sm:flex-row sm:items-center justify-between gap-6 p-4 bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700/50'>
                <div className='flex-1'>
                    <Input 
                        label='Available Slots' 
                        type='number' 
                        name='acc_spaces'
                        value={currentOption.spacesLeft}
                        onChange={(e: any) => setCurrentOption({...currentOption, spacesLeft: Number(e.target.value)})}
                        placeholder='0 for Unlimited'
                        customClass='!border-none !bg-gray-50 dark:!bg-gray-700/50'
                    />
                </div>
                <div className='flex items-center gap-3 shrink-0 pt-2 sm:pt-0'>
                    <label className='relative inline-flex items-center gap-3 cursor-pointer'>
                        <span className='text-sm font-bold text-gray-700 dark:text-gray-300 uppercase tracking-tight'>
                            Enable Sale Price
                        </span>
                        <input 
                            type='checkbox' 
                            checked={currentOption.isOnSale || false}
                            onChange={(e: ChangeEvent<HTMLInputElement>) => setCurrentOption({...currentOption, isOnSale: e.target.checked})}
                            className='sr-only peer'
                        />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 dark:peer-focus:ring-primary-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-primary-600" />
                    </label>
                </div>
            </div>

            <div className='flex justify-end gap-3 pt-2'>
                <Button 
                    variant='ghost' 
                    size='sm'
                    onClick={() => setIsAdding(false)}
                    className='font-bold text-xs uppercase'
                >
                    Cancel
                </Button>
                <Button 
                    variant='primary' 
                    size='sm'
                    onClick={handleAdd}
                    disabled={!currentOption.name || !currentOption.price}
                    className='!px-6 font-bold'
                >
                    Add Room Type
                </Button>
            </div>
        </div>
      )}
    </div>
  );
};

export default AccommodationManager;
